import React, { useState } from 'react';
import ContactModal from '../components/ContactModal';
import './ChessForSchools.css';

const ChessForSchools = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeBenefit, setActiveBenefit] = useState(0);

    const benefits = [
        {
            title: "Enhanced Critical Thinking",
            description: "Chess teaches students to think several moves ahead, analyzing consequences and planning strategically. This skill translates directly to academic subjects like mathematics, science, and problem-solving tasks, helping students excel in their studies and develop analytical minds that serve them throughout life."
        },
        {
            title: "Improved Concentration & Focus",
            description: "A single chess game requires sustained attention and focus. Students who play chess regularly develop the ability to concentrate for extended periods, resist distractions, and maintain mental clarity—skills that are invaluable in the classroom and during examinations."
        },
        {
            title: "Boosted Memory & Pattern Recognition",
            description: "Chess players must remember opening sequences, tactical patterns, and endgame techniques. This constant exercise strengthens memory retention and pattern recognition abilities, helping students perform better across all academic subjects that require memorization and recall."
        },
        {
            title: "Emotional Intelligence & Resilience",
            description: "Chess teaches students to handle both victory and defeat gracefully. They learn to manage frustration, stay calm under pressure, and bounce back from setbacks. These emotional skills are crucial for personal development and success in all areas of life."
        },
        {
            title: "Social Skills & Sportsmanship",
            description: "Through chess tournaments and club activities, students learn to interact respectfully with peers, follow rules, and display good sportsmanship. They build friendships across age groups and backgrounds, developing social confidence and communication skills."
        },
        {
            title: "Professional Opportunities & Earnings",
            description: "Chess opens doors to represent corporates, participate in sponsored tournaments, and earn prize money while still in school. Some of our students already represent companies in tournaments, earning income doing what they love. It's not just a game—it's a pathway to real opportunities."
        }
    ];

    return (
        <div className="page-container">
            {/* Hero Section */}
            <section className="page-hero-section schools-hero">
                <div className="page-hero-content">
                    <p className="page-hero-subtitle">CHESS FOR SCHOOLS</p>
                    <h1 className="page-hero-title">
                        Empower Your Students<br />
                        Through Chess
                    </h1>
                    <p className="page-hero-description">
                        Chess isn't just a game—it's a tool for developing critical thinking, focus, and resilience in students. By integrating chess into your school, you'll boost your institution's reputation while giving your students skills that last a lifetime.
                    </p>
                    <div className="hero-cta">
                        <button
                            className="btn-cta-primary"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Get Your Students Coached
                        </button>
                    </div>
                </div>
            </section>

            {/* School Popularity Section */}
            <section className="school-popularity-section">
                <div className="container">
                    <h2>Elevate Your School's Reputation</h2>
                    <p className="section-intro">
                        Chess programs don't just benefit students—they transform your school's image and market position. Here's how chess can put your institution on the map.
                    </p>

                    <div className="popularity-cards-grid">
                        <div className="popularity-card">
                            <div className="popularity-icon">📢</div>
                            <h3>Free Marketing Through Wins</h3>
                            <p>
                                When your students excel in chess tournaments, your school gets featured in media coverage, tournament announcements, and award ceremonies. Every trophy your students bring home is free publicity that showcases your commitment to holistic education. Parents, sponsors, and the community take notice when your school produces champions.
                            </p>
                        </div>

                        <div className="popularity-card">
                            <div className="popularity-icon">🎯</div>
                            <h3>Attract Quality-Conscious Parents</h3>
                            <p>
                                Modern parents actively seek schools offering meaningful extracurricular activities that develop their children's minds. Our chess program addresses key parent concerns: academic enhancement, character building, and competitive opportunities. By offering structured chess training, you position your school as an institution that cares about comprehensive student development, not just test scores.
                            </p>
                        </div>

                        <div className="popularity-card">
                            <div className="popularity-icon">🌍</div>
                            <h3>Local & International Exposure</h3>
                            <p>
                                Chess tournaments happen at every level—from local inter-school competitions to international championships. Your school's name appears on scoreboards, websites, and tournament materials across Kenya and beyond. We've had students compete in Mombasa, Kisumu, Nairobi, and internationally in Serbia, Zimbabwe, and the USA. That's global visibility for your institution.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Carousel Section */}
            <section className="benefits-carousel-section">
                <div className="container">
                    <h2>How Chess Transforms Student Development</h2>
                    <p className="section-intro">
                        Chess is more than a game—it's a comprehensive development tool that builds cognitive, emotional, and social skills in students.
                    </p>

                    <div className="carousel-container">
                        <div className="carousel-content">
                            <div className="benefit-display">
                                <div className="benefit-icon">🧠</div>
                                <h3>{benefits[activeBenefit].title}</h3>
                                <p>{benefits[activeBenefit].description}</p>
                            </div>
                        </div>

                        <div className="carousel-controls">
                            {benefits.map((benefit, index) => (
                                <button
                                    key={index}
                                    className={`carousel-btn ${activeBenefit === index ? 'active' : ''}`}
                                    onClick={() => setActiveBenefit(index)}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>

                        <div className="carousel-navigation">
                            <button
                                className="nav-btn prev"
                                onClick={() => setActiveBenefit((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                            >
                                ← Previous
                            </button>
                            <button
                                className="nav-btn next"
                                onClick={() => setActiveBenefit((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Implementation Methods Section */}
            <section className="implementation-section">
                <div className="container">
                    <h2>How to Integrate Chess Into Your School</h2>
                    <p className="section-intro">
                        We offer flexible implementation options that fit seamlessly into your school's structure and schedule.
                    </p>

                    <div className="implementation-cards-grid">
                        <div className="implementation-card">
                            <div className="implementation-icon">📚</div>
                            <h3>Curriculum-Based Lessons</h3>
                            <p>
                                Integrate chess as a formal class within your curriculum. Students receive structured lessons during school hours, complete with assessments, progress tracking, and certificates. This approach ensures every student gets exposure to chess and its cognitive benefits, making it part of their core educational experience.
                            </p>
                            <ul className="implementation-features">
                                <li>Scheduled class periods</li>
                                <li>Structured curriculum</li>
                                <li>Progress assessments</li>
                                <li>Certificates of completion</li>
                            </ul>
                        </div>

                        <div className="implementation-card">
                            <div className="implementation-icon">⚡</div>
                            <h3>Extracurricular Club</h3>
                            <p>
                                Establish chess as an after-school club or extracurricular activity. This option allows passionate students to dive deeper into competitive chess, participate in tournaments, and represent your school in inter-school competitions. Perfect for schools wanting to build a competitive chess team.
                            </p>
                            <ul className="implementation-features">
                                <li>After-school sessions</li>
                                <li>Tournament preparation</li>
                                <li>Competitive team building</li>
                                <li>Flexible scheduling</li>
                            </ul>
                        </div>
                    </div>

                    <div className="section-cta">
                        <button
                            className="btn-cta-primary"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Start a Chess Program
                        </button>
                    </div>
                </div>
            </section>

            <ContactModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </div>
    );
};

export default ChessForSchools;
